import { useCallback, useMemo, useRef, useEffect, useState } from 'react'
import { IScenario, IUseScenario } from '../screens/BookingForm/types'
import { useBookingContext } from '../providers/BookingFormProvider/BookingFormProvider'
import { useWording } from './useWording'
import { useDispatch } from '@wordpress/data'
import { store_name } from '../../store/frontend'

export const useScenario = (scenarios: IScenario[]) => {
    const { formData, services, paymentMethods } = useBookingContext()
    const wording = useWording()
    const filteredScenarios = useMemo(
        () =>
            scenarios.filter(({ isVisible }: IScenario) => {
                if (isVisible === undefined) return true
                return isVisible()
            }),
        [scenarios, paymentMethods]
    )

    // Always keep the latest filteredScenarios in a ref
    const filteredScenariosRef = useRef(filteredScenarios)
    useEffect(() => {
        filteredScenariosRef.current = filteredScenarios
    }, [filteredScenarios])

    const [currentIndex, setCurrentIndex] = useState(0)
    const currentIndexRef = useRef(0)
    useEffect(() => {
        currentIndexRef.current = currentIndex
    }, [currentIndex])

    const currentScenario = filteredScenarios[currentIndex]
    const { placeBooking } = useDispatch(store_name)

    const title =
        typeof currentScenario.title === 'string'
            ? wording?.[currentScenario.title] || currentScenario.title
            : currentScenario.title
    const description =
        typeof currentScenario.description === 'string'
            ? wording?.[currentScenario.description] ||
              currentScenario.description
            : currentScenario.description

    const { canGoNext, nextStepError } = useMemo(() => {
        let firstError: string | null = null
        const allValid = Object.entries(currentScenario.validationRules).every(
            ([field, validator]) => {
                const value = formData[field]
                const result =
                    value !== undefined && value !== null
                        ? validator(value)
                        : validator(formData)
                if (result !== true && !firstError) {
                    firstError = typeof result === 'string' ? result : null
                }
                return result === true
            }
        )
        return {
            canGoNext: allValid,
            nextStepError:
                (typeof firstError === 'string' && wording?.[firstError]) ||
                firstError,
        }
    }, [formData, currentScenario])

    const canGoNextRef = useRef(canGoNext)
    const formDataRef = useRef(formData)

    useEffect(() => {
        canGoNextRef.current = canGoNext
    }, [canGoNext])

    useEffect(() => {
        formDataRef.current = formData
    }, [formData])

    const goBack = useCallback(() => {
        setCurrentIndex((prev) => Math.max(prev - 1, 0))
    }, [])

    const goNext = useCallback(async () => {
        if (!canGoNextRef.current) return

        const index = currentIndexRef.current
        const scenarios = filteredScenariosRef.current

        if (index === scenarios.length - 1) {
            const response = await placeBooking(formDataRef.current)
            return
        }

        setCurrentIndex((prev) => Math.min(prev + 1, scenarios.length - 1))
    }, [placeBooking])

    const stepInto = useCallback((index: number) => {
        setCurrentIndex(index)
    }, [])

    useEffect(() => {
        const selectedServices = services.filter((service) => service.selected)
        if (!selectedServices || selectedServices.length === 0) {
            setCurrentIndex(0)
        }
    }, [services])

    return {
        ...currentScenario,
        title,
        description,
        totalSteps: filteredScenarios.length,
        currentIndex,
        canGoNext,
        goBack,
        goNext,
        stepInto,
        nextStepError,
    } as IUseScenario
}
